import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Heart, Minus, Plus, ShoppingBag, Zap } from 'lucide-react'
import { showErrorToast, showSuccessToast } from '@/lib/toast'
import type { Product } from '@/types'
import { formatCurrency, stockLimitMessage } from '@/lib/utils'
import { useWishlistStore } from '@/store/stores/wishlistStore'
import { useCartStore } from '@/store/stores/cartStore'
import { Button } from '@/components/ui/Button'
import { StoreImage } from '@/store/components/StoreImage'
import { cn } from '@/lib/utils'
import {
  pickProductSize,
  availableProductSizes,
  type ProductSizePick,
} from '@/lib/productSize'
import { discountPercent, getSizeVariant, productDisplayImageUrls, productVariants, variantImage } from '@/lib/productVariant'
import { productHasAudience } from '@/lib/productAudience'
import { ProductAudienceBadge } from '@/store/components/ProductAudienceBadge'
import { productDetailLinkState } from '@/lib/productRoute'
import { useProductGridTitleSync } from '@/store/components/ProductGridTitleSync'
import { ProductRatingBadge, productHasStoreRating } from '@/store/components/ProductRatingBadge'

interface ProductCardProps {
  product: Product
  className?: string
  showActions?: boolean
  /** How the initial variant is chosen (home sections: 1st/4th in-stock, others random). */
  defaultSizePick?: ProductSizePick
  /** When set, variant selection is controlled by the parent (shop sort/filter). */
  selectedSize?: string
  onSizeChange?: (size: string) => void
  /** Brief gold pulse after price-sort reposition (shop page). */
  sortHighlighted?: boolean
  sortHighlightToken?: number
  /** Eager-load image (first visible row on home/shop). */
  imagePriority?: boolean
  /** Homepage: single green star with rating inside. */
  ratingVariant?: 'default' | 'home-star'
}

export function ProductCard({
  product,
  className,
  showActions,
  defaultSizePick = 'first-in-stock',
  selectedSize,
  onSizeChange,
  sortHighlighted = false,
  sortHighlightToken,
  imagePriority = false,
  ratingVariant = 'default',
}: ProductCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const titleMeasureRef = useRef<HTMLSpanElement>(null)
  const titleSync = useProductGridTitleSync()
  const titleLines = titleSync?.titleLines ?? 1
  const reportTitleLines = titleSync?.reportTitleLines
  const navigate = useNavigate()
  const addItem = useCartStore((s) => s.addItem)
  const startBuyNow = useCartStore((s) => s.startBuyNow)
  const { has, toggle } = useWishlistStore()
  const [internalSize, setInternalSize] = useState<string>(() =>
    pickProductSize(product, defaultSizePick)
  )
  const [qty, setQty] = useState(1)

  const isControlled = selectedSize !== undefined
  const size = isControlled ? selectedSize : internalSize

  const updateSize = (next: string) => {
    if (!isControlled) {
      setInternalSize(next)
    }
    onSizeChange?.(next)
  }

  const wished = has(product.id)
  const hasSizes = Boolean(product.sizes?.length)

  const priceInfo = useMemo(() => {
    if (hasSizes) {
      const v = product.sizes!.find((s) => s.size === size)
      return {
        price: v?.discountPrice ?? v?.price ?? product.price,
        originalPrice: v?.price ?? product.price,
        hasDiscount: Boolean(v?.discountPrice),
        stock: v?.quantity ?? 0,
      }
    }
    return {
      price: product.discountPrice ?? product.price,
      originalPrice: product.price,
      hasDiscount: Boolean(product.discountPrice),
      stock: product.quantity,
    }
  }, [product, size, hasSizes])

  const outOfStock = priceInfo.stock <= 0
  const percentOff = priceInfo.hasDiscount
    ? discountPercent(priceInfo.price, priceInfo.originalPrice)
    : 0
  const selectedVariant = hasSizes ? getSizeVariant(product, size) : undefined
  const displayImage =
    (hasSizes ? variantImage(selectedVariant, product) : product.images[0]) ||
    product.images[0]
  const cardImageUrls = useMemo(() => productDisplayImageUrls(product), [product])

  useEffect(() => {
    setQty((current) => Math.min(current, Math.max(1, priceInfo.stock)))
  }, [size, priceInfo.stock])

  useEffect(() => {
    if (!hasSizes) return
    const available = availableProductSizes(product)
    if (size && available.includes(size)) return
    updateSize(pickProductSize(product, defaultSizePick))
  }, [product, hasSizes, size, defaultSizePick])

  useLayoutEffect(() => {
    const el = titleMeasureRef.current
    if (!el || !reportTitleLines) return

    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight)
      const lines: 1 | 2 =
        Number.isFinite(lineHeight) && lineHeight > 0
          ? el.scrollHeight / lineHeight > 1.05
            ? 2
            : 1
          : 1
      reportTitleLines(product.id, lines)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [product.id, product.name, reportTitleLines])

  useLayoutEffect(() => {
    const el = cardRef.current
    if (!el) return
    if (!sortHighlighted || sortHighlightToken == null) {
      el.classList.remove('product-card-sort-highlight')
      return
    }
    el.classList.remove('product-card-sort-highlight')
    void el.getBoundingClientRect()
    el.classList.add('product-card-sort-highlight')
  }, [sortHighlighted, sortHighlightToken])

  const handleIncreaseQty = () => {
    if (qty >= priceInfo.stock) {
      showErrorToast(stockLimitMessage(priceInfo.stock))
      return
    }
    setQty(qty + 1)
  }

  const handleAddToCart = () => {
    if (outOfStock) {
      showErrorToast('Out of stock')
      return
    }
    addItem(product, hasSizes ? size : undefined, qty)
    showSuccessToast('Added to cart')
  }

  const handleBuyNow = () => {
    if (outOfStock) {
      showErrorToast('Out of stock')
      return
    }
    startBuyNow(product, hasSizes ? size : undefined, qty)
    navigate('/checkout', { state: { buyNow: true } })
  }

  return (
    <motion.div
      ref={cardRef}
      id={`product-card-${product.id}`}
      whileHover={{ y: -4 }}
      className={cn(
        'group glass-card gold-border-glow overflow-hidden scroll-mt-24 flex h-full flex-col',
        className
      )}
    >
      <Link
        to={`/shop/${product.id}`}
        state={productDetailLinkState(product)}
        className="block relative aspect-[4/5] overflow-hidden bg-luxury-800"
      >
        {cardImageUrls.length > 0 ? (
          cardImageUrls.map((url) => {
            const isActive = url === displayImage
            return (
              <motion.div
                key={url}
                initial={false}
                animate={{ opacity: isActive ? 1 : 0, scale: isActive ? 1 : 1.02 }}
                transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                className="absolute inset-0"
                style={{
                  zIndex: isActive ? 1 : 0,
                  pointerEvents: isActive ? 'auto' : 'none',
                }}
                aria-hidden={!isActive}
              >
                <StoreImage
                  src={url}
                  alt={product.name}
                  priority={imagePriority && url === cardImageUrls[0]}
                  containerClassName="h-full w-full"
                  className="transition-transform duration-500 group-hover:scale-105"
                  fallback={
                    <span className="font-display text-2xl text-zinc-600">BEM</span>
                  }
                />
              </motion.div>
            )
          })
        ) : (
          <StoreImage
            src={displayImage}
            alt={product.name}
            priority={imagePriority}
            containerClassName="h-full w-full"
            className="transition-transform duration-500 group-hover:scale-105"
            fallback={
              <span className="font-display text-2xl text-zinc-600">BEM</span>
            }
          />
        )}
        {product.featured && (
          <span className="absolute left-3 top-3 z-10 rounded-full bg-gold-500/90 px-2.5 py-0.5 text-[10px] font-bold uppercase text-luxury-950">
            Featured
          </span>
        )}
        {(productHasAudience(product) || productHasStoreRating(product)) && (
          <div className="absolute inset-x-3 bottom-3 z-20 flex items-center justify-between gap-2">
            <div className="flex h-8 items-center">
              {productHasAudience(product) && (
                <ProductAudienceBadge
                  audience={product.audience}
                  placement="on-image"
                />
              )}
            </div>
            <div className="pointer-events-none flex h-8 items-center">
              {productHasStoreRating(product) && (
                <ProductRatingBadge
                  product={product}
                  variant={ratingVariant === 'home-star' ? 'home-star' : 'on-image'}
                  showCount={false}
                />
              )}
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault()
            toggle(product.id)
          }}
          className={cn(
            'absolute right-3 top-3 z-10 rounded-full p-2 transition-colors shrink-0',
            wished
              ? 'bg-gold-500 text-luxury-950'
              : 'bg-black/50 text-white hover:bg-gold-500/80'
          )}
        >
          <Heart className={cn('h-4 w-4', wished && 'fill-current')} />
        </button>
        {outOfStock && (
          <span className="absolute inset-0 z-30 flex items-center justify-center bg-black/60 text-sm font-medium text-zinc-300">
            Out of Stock
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <p className="text-[10px] uppercase tracking-wider text-gold-500/80">
          {product.category}
        </p>
        <div className="relative mt-1">
          <span
            ref={titleMeasureRef}
            aria-hidden
            className="pointer-events-none invisible absolute inset-x-0 top-0 block font-display text-lg font-semibold line-clamp-2"
          >
            {product.name}
          </span>
          <Link to={`/shop/${product.id}`} state={productDetailLinkState(product)}>
            <h3
              className={cn(
                'font-display text-lg font-semibold text-zinc-100 hover:text-gold-300 transition-colors line-clamp-2',
                titleLines === 2 ? 'h-12' : 'h-6'
              )}
            >
              {product.name}
            </h3>
          </Link>
        </div>
        <p
          className={cn(
            'text-xs text-zinc-500 mt-0.5 truncate h-4',
            !product.subtitle?.trim() && 'invisible'
          )}
        >
          {product.subtitle?.trim() || '\u00A0'}
        </p>
        <div className="mt-3 flex min-h-6 flex-wrap items-baseline gap-2">
          <span className="font-semibold text-gold-300">{formatCurrency(priceInfo.price)}</span>
          {priceInfo.hasDiscount && (
            <span className="text-xs text-zinc-500 line-through">
              {formatCurrency(priceInfo.originalPrice)}
            </span>
          )}
          {percentOff > 0 && (
            <span className="rounded-md bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">
              {percentOff}% off
            </span>
          )}
        </div>

        {showActions && (
          <div className="mt-3 h-[3.75rem]">
            {hasSizes ? (
              <>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                  Variant
                </p>
                <div className="flex flex-nowrap gap-1 sm:gap-1.5">
                  {productVariants(product).map((v) => (
                    <button
                      key={v.size}
                      type="button"
                      onClick={() => updateSize(v.size)}
                      className={cn(
                        'variant-chip',
                        size === v.size && 'variant-chip-active'
                      )}
                    >
                      {v.size}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="invisible" aria-hidden>
                <p className="text-[10px] uppercase tracking-wider mb-1.5">Variant</p>
                <div className="h-[26px] rounded-lg border border-transparent" />
              </div>
            )}
          </div>
        )}

        {showActions && (
          <div className="mt-3 flex items-center justify-between gap-2">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Qty</p>
            <div className="flex items-center rounded-lg border border-luxury-600">
              <button
                type="button"
                className="p-1.5 text-zinc-400 hover:text-gold-300 disabled:opacity-40"
                disabled={outOfStock || qty <= 1}
                onClick={() => setQty(Math.max(1, qty - 1))}
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="w-8 text-center text-sm text-zinc-200">{qty}</span>
              <button
                type="button"
                className="p-1.5 text-zinc-400 hover:text-gold-300 disabled:opacity-40"
                disabled={outOfStock}
                onClick={handleIncreaseQty}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}

        {showActions && (
          <div className="mt-auto flex flex-col gap-2 pt-3">
            <Button
              size="sm"
              className="w-full"
              disabled={outOfStock}
              onClick={handleAddToCart}
            >
              <ShoppingBag className="h-3.5 w-3.5" /> Add to Cart
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="w-full"
              disabled={outOfStock}
              onClick={handleBuyNow}
            >
              <Zap className="h-3.5 w-3.5" /> Buy Now
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  )
}
